/**
 * EmailRuleEditor — create/edit a single mailbox-scoped filter rule.
 */
import { h } from 'preact'
import { useState } from 'preact/hooks'
import { RequiredAsterisk } from '../shared/RequiredAsterisk.jsx'

const CONDITION_FIELDS = ['from', 'to', 'subject', 'body', 'has_attachment']
const CONDITION_OPERATORS = ['contains', 'not_contains', 'equals', 'starts_with', 'ends_with', 'regex', 'is_empty']
const ACTION_TYPES = [
  'move_to_folder', 'add_tag', 'assign_responsible', 'mark_read', 'archive',
  'block_sender', 'set_priority', 'auto_reply', 'forward_to',
]
const NO_VALUE_ACTIONS = new Set(['mark_read', 'archive', 'block_sender'])
// has_attachment is a boolean-ish field — string operators like contains/regex
// don't make sense for it, only "is/equals" and "is empty" do.
const HAS_ATTACHMENT_OPERATORS = ['equals', 'is_empty']

// Stable per-row ids for React/Preact `key` — array index breaks focus/state
// when a row is removed from the middle of the list.
let _ruleItemSeq = 0
function nextItemId() {
  _ruleItemSeq += 1
  return `rule-item-${_ruleItemSeq}`
}

const emptyCondition = () => ({ _key: nextItemId(), field: 'subject', operator: 'contains', value: '' })
const emptyAction = () => ({ _key: nextItemId(), type: 'mark_read', value: '' })

function operatorsFor(field) {
  return field === 'has_attachment' ? HAS_ATTACHMENT_OPERATORS : CONDITION_OPERATORS
}

export function EmailRuleEditor({ store, i18n, mailboxId, rule }) {
  const isNew = !rule
  const folders = (store.emailFolders.value || []).filter(f => f.mailbox_id === mailboxId)
  const tags = (store.emailTags.value || []).filter(t => t.mailbox_id === mailboxId)
  const people = store.peopleWithUser.value || []

  const [name, setName] = useState(rule?.name || '')
  const [conditionMode, setConditionMode] = useState(rule?.condition_mode || 'all')
  const [conditions, setConditions] = useState(
    rule?.conditions?.length
      ? rule.conditions.map(c => ({ _key: nextItemId(), ...c }))
      : [emptyCondition()]
  )
  const [actions, setActions] = useState(
    rule?.actions?.length
      ? rule.actions.map(a => ({ _key: nextItemId(), ...a }))
      : [emptyAction()]
  )
  const [stopProcessing, setStopProcessing] = useState(!!rule?.stop_processing)
  const [active, setActive] = useState(rule ? !!rule.active : true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  function updateCondition(index, patch) {
    setConditions(conditions.map((c, i) => (i === index ? { ...c, ...patch } : c)))
  }

  function updateAction(index, patch) {
    setActions(actions.map((a, i) => (i === index ? { ...a, ...patch } : a)))
  }

  function actionValueInput(action, index) {
    if (NO_VALUE_ACTIONS.has(action.type)) {
      return <span class="form-text">{i18n?.t('emails.email_rules.form.no_value_needed')}</span>
    }
    if (action.type === 'move_to_folder') {
      return (
        <select
          class="form-select form-select-sm"
          value={action.value || ''}
          onChange={e => updateAction(index, { value: e.target.value })}
        >
          <option value="">{i18n?.t('emails.email_rules.form.choose_action')}</option>
          {folders.map(f => <option key={f.id} value={String(f.id)}>{f.name}</option>)}
        </select>
      )
    }
    if (action.type === 'add_tag') {
      return (
        <select
          class="form-select form-select-sm"
          value={action.value || ''}
          onChange={e => updateAction(index, { value: e.target.value })}
        >
          <option value="">—</option>
          {tags.map(t => <option key={t.id} value={String(t.id)}>{t.tag}</option>)}
        </select>
      )
    }
    if (action.type === 'assign_responsible') {
      return (
        <select
          class="form-select form-select-sm"
          value={action.value || ''}
          onChange={e => updateAction(index, { value: e.target.value })}
        >
          <option value="">—</option>
          {people.map(p => <option key={p.id} value={String(p.id)}>{p.name}</option>)}
        </select>
      )
    }
    if (action.type === 'set_priority') {
      return (
        <select
          class="form-select form-select-sm"
          value={action.value || ''}
          onChange={e => updateAction(index, { value: e.target.value })}
        >
          <option value="">—</option>
          {['high', 'medium', 'low'].map(p => (
            <option key={p} value={p}>{i18n?.t(`emails.email_rules.priorities.${p}`)}</option>
          ))}
        </select>
      )
    }
    return (
      <input
        type="text"
        class="form-control form-control-sm"
        value={action.value || ''}
        placeholder={
          action.type === 'forward_to'
            ? i18n?.t('emails.email_rules.form.forward_placeholder')
            : action.type === 'auto_reply'
              ? i18n?.t('emails.email_rules.form.auto_reply_placeholder')
              : i18n?.t('emails.email_rules.form.action_placeholder')
        }
        onInput={e => updateAction(index, { value: e.target.value })}
      />
    )
  }

  async function handleSubmit(e) {
    e.preventDefault()
    if (!conditions.length) {
      setError(i18n?.t('emails.email_rules.form.conditions_required') || 'At least one condition is required')
      return
    }
    setSaving(true)
    setError(null)
    try {
      await store.saveRule({
        id: rule?.id,
        name: name.trim(),
        mailbox_id: mailboxId,
        condition_mode: conditionMode,
        conditions: conditions.map(c => ({
          field: c.field,
          operator: c.operator,
          value: c.operator === 'is_empty' ? '' : (c.value || ''),
        })),
        actions: actions
          .filter(a => a.type)
          .map(a => ({
            type: a.type,
            value: NO_VALUE_ACTIONS.has(a.type) ? '1' : (a.value || ''),
          })),
        stop_processing: stopProcessing,
        active,
      })
    } catch (err) {
      console.error('[EmailRuleEditor] save failed', err)
      setError(err.message || 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} class="email-rule-editor">
      {error && <div class="alert alert-danger py-2">{error}</div>}

      <div class="card mb-3">
        <div class="card-body">
          <h6 class="card-title">{i18n?.t('emails.email_rules.form.basic_info')}</h6>
          <div class="row g-3">
            <div class="col-lg-7">
              <label class="form-label fw-medium">
                {i18n?.t('emails.email_rules.form.name_label')}
                <RequiredAsterisk />
              </label>
              <input
                class="form-control"
                required
                value={name}
                placeholder={i18n?.t('emails.email_rules.form.name_placeholder')}
                onInput={e => setName(e.target.value)}
              />
            </div>
            <div class="col-lg-5">
              <label class="form-label fw-medium">{i18n?.t('emails.email_rules.form.condition_mode_label')}</label>
              <select
                class="form-select"
                value={conditionMode}
                onChange={e => setConditionMode(e.target.value)}
              >
                <option value="all">{i18n?.t('emails.email_rules.form.condition_mode_all')}</option>
                <option value="any">{i18n?.t('emails.email_rules.form.condition_mode_any')}</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <div class="card mb-3">
        <div class="card-body">
          <h6 class="card-title">{i18n?.t('emails.email_rules.form.conditions_title')}</h6>
          {conditions.map((cond, i) => (
            <div key={cond._key} class="row g-2 mb-2 align-items-center">
              <div class="col-md-4">
                <select
                  class="form-select form-select-sm"
                  value={cond.field}
                  onChange={e => {
                    const field = e.target.value
                    const allowedOps = operatorsFor(field)
                    const patch = { field }
                    if (!allowedOps.includes(cond.operator)) patch.operator = allowedOps[0]
                    updateCondition(i, patch)
                  }}
                >
                  {CONDITION_FIELDS.map(f => (
                    <option key={f} value={f}>{i18n?.t(`emails.email_rules.conditions.fields.${f}`)}</option>
                  ))}
                </select>
              </div>
              <div class="col-md-3">
                <select
                  class="form-select form-select-sm"
                  value={cond.operator}
                  onChange={e => updateCondition(i, { operator: e.target.value })}
                >
                  {operatorsFor(cond.field).map(op => (
                    <option key={op} value={op}>{i18n?.t(`emails.email_rules.conditions.operators.${op}`)}</option>
                  ))}
                </select>
              </div>
              <div class="col-md-4">
                {cond.operator === 'is_empty' ? (
                  <span class="form-text">{i18n?.t('emails.email_rules.form.no_value_needed')}</span>
                ) : (
                  <input
                    type="text"
                    class="form-control form-control-sm"
                    value={cond.value || ''}
                    placeholder={i18n?.t('emails.email_rules.form.condition_value_placeholder')}
                    onInput={e => updateCondition(i, { value: e.target.value })}
                  />
                )}
              </div>
              <div class="col-md-1">
                <button
                  type="button"
                  class="btn btn-sm btn-outline-danger"
                  disabled={conditions.length <= 1}
                  onClick={() => setConditions(conditions.filter((_, j) => j !== i))}
                >
                  <i class="bi bi-x" />
                </button>
              </div>
            </div>
          ))}
          <button
            type="button"
            class="btn btn-sm btn-outline-secondary mt-1"
            onClick={() => setConditions([...conditions, emptyCondition()])}
          >
            <i class="bi bi-plus" /> {i18n?.t('emails.email_rules.form.add_condition')}
          </button>
        </div>
      </div>

      <div class="card mb-3">
        <div class="card-body">
          <h6 class="card-title">{i18n?.t('emails.email_rules.form.actions_title')}</h6>
          {actions.map((action, i) => (
            <div key={action._key} class="row g-2 mb-2 align-items-center">
              <div class="col-md-5">
                <select
                  class="form-select form-select-sm"
                  value={action.type}
                  onChange={e => updateAction(i, { type: e.target.value, value: '' })}
                >
                  {ACTION_TYPES.map(t => (
                    <option key={t} value={t}>{i18n?.t(`emails.email_rules.actions.types.${t}`)}</option>
                  ))}
                </select>
              </div>
              <div class="col-md-6">{actionValueInput(action, i)}</div>
              <div class="col-md-1">
                <button
                  type="button"
                  class="btn btn-sm btn-outline-danger"
                  disabled={actions.length <= 1}
                  onClick={() => setActions(actions.filter((_, j) => j !== i))}
                >
                  <i class="bi bi-x" />
                </button>
              </div>
            </div>
          ))}
          <button
            type="button"
            class="btn btn-sm btn-outline-secondary mt-1"
            onClick={() => setActions([...actions, emptyAction()])}
          >
            <i class="bi bi-plus" /> {i18n?.t('emails.email_rules.form.add_action')}
          </button>
        </div>
      </div>

      <div class="card mb-3">
        <div class="card-body">
          <h6 class="card-title">{i18n?.t('emails.email_rules.form.options_title')}</h6>
          <div class="form-check mb-2">
            <input
              id="rule-stop"
              type="checkbox"
              class="form-check-input"
              checked={stopProcessing}
              onChange={e => setStopProcessing(e.target.checked)}
            />
            <label class="form-check-label" for="rule-stop">
              {i18n?.t('emails.email_rules.form.stop_processing_label')}
            </label>
          </div>
          <div class="form-check">
            <input
              id="rule-active"
              type="checkbox"
              class="form-check-input"
              checked={active}
              onChange={e => setActive(e.target.checked)}
            />
            <label class="form-check-label" for="rule-active">
              {i18n?.t('emails.email_rules.form.active_label')}
            </label>
          </div>
        </div>
      </div>

      <div class="d-flex gap-2 justify-content-center mt-2">
        <button type="submit" class="btn btn-primary" disabled={saving || !name.trim()}>
          {isNew
            ? i18n?.t('emails.email_rules.form.submit_create')
            : i18n?.t('emails.email_rules.form.submit_update')}
        </button>
        <button type="button" class="btn btn-outline-secondary" onClick={() => store.cancelRuleEdit()}>
          {i18n?.t('emails.email_rules.form.cancel')}
        </button>
      </div>
    </form>
  )
}
